import { Logo } from "@/components";
import { AiChat, LanguagePicker, UserMenu } from "@/features";
import { AuthProtectedRoute } from "@/features/auth";
import { useTranslation } from "@/hooks";
import { AI } from "@/services/ai";
import { useSignOut } from "@/stores/auth";
import { useSidebar } from "@/stores/ui";
import { Flex, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router";
import "./MainLayout.css";

export function MainLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const [activePrompt, setActivePrompt] = useState<boolean>(false);
  const sidebar = useSidebar();

  const handleSignOut = () => {
    signOut().then(() => {
      navigate("/login");
    });
  };

  const navLinks = [
    {
      path: "/discover",
      title: t("nav").discover,
    },
    {
      path: "/build",
      title: t("nav").build,
    },
    {
      path: "/my-data",
      title: t("nav").myData,
    },
  ];

  const isSidebarOpen = sidebar.mode !== null;

  useEffect(() => {
    if (isSidebarOpen) {
      setActivePrompt(false);
    }
  }, [isSidebarOpen]);

  function onFocusAiChat() {
    setActivePrompt(true);
  }

  function onBlurAiChat() {
    setActivePrompt(false);
  }

  async function onAiPrompt(prompt: string) {
    await AI.prompt(prompt);
  }

  return (
    <AuthProtectedRoute>
      <header className="main-header">
        <Flex justify="center" w="100%" h="100%">
          <Flex flex={1} align="center" justify="flex-start" gap="lg">
            <Link to="/" className="main-logo">
              <Logo size={22} />
              <Text fz={14} fw={600}>
                Atlasio
              </Text>
            </Link>
          </Flex>
          <Flex align="center" justify="center" gap="xs" h="100%">
            {navLinks.map((link) => {
              const isActive = location.pathname.startsWith(link.path);
              return (
                <Link to={link.path} key={link.path} style={{ height: "100%" }}>
                  <Flex
                    key={link.path}
                    h="100%"
                    direction="column"
                    align="center"
                    justify="center"
                    px="lg"
                    className="main-header-link"
                    style={{
                      borderColor: isActive
                        ? "var(--mantine-color-eu_gold-6)"
                        : "transparent",
                    }}
                  >
                    <Text fz={14} fw={isActive ? 600 : 400} c="white">
                      {link.title}
                    </Text>
                  </Flex>
                </Link>
              );
            })}
          </Flex>
          <Flex flex={1} align="center" justify="flex-end" gap="lg">
            <LanguagePicker />
            <UserMenu onSignOut={handleSignOut} />
          </Flex>
        </Flex>
      </header>
      <Outlet />
      <AiChat
        autoFocus
        collapsed={!activePrompt}
        placeholder={t("discover").whatDoYouWantToDo}
        onFocus={onFocusAiChat}
        onBlur={onBlurAiChat}
        onPrompt={onAiPrompt}
      />
    </AuthProtectedRoute>
  );
}
